import { useEffect, useRef } from 'react';

/*
 * Wireframe moon with stippled "maria", orbiting satellites, and pointer
 * parallax: mousemove over the hero writes a target, a rAF loop eases the
 * moon (and satellites, at a deeper rate) toward it.
 */
export default function Moon() {
  const moonRef = useRef(null);
  const satsRef = useRef(null);

  useEffect(() => {
    const moonEl = moonRef.current;
    const satsEl = satsRef.current;
    const reduceMotion = !!(
      window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches
    );
    if (reduceMotion) {
      if (satsEl && satsEl.pauseAnimations) satsEl.pauseAnimations();
      return;
    }

    let raf = null;
    let tnx = 0;
    let tny = 0;
    let pnx = 0;
    let pny = 0;

    function tick() {
      pnx += (tnx - pnx) * 0.08;
      pny += (tny - pny) * 0.08;
      if (Math.abs(tnx - pnx) > 0.001 || Math.abs(tny - pny) > 0.001 || tnx || tny) {
        moonEl.style.transform =
          'translate(' + (pnx * 8).toFixed(2) + 'px,' + (pny * 6).toFixed(2) + 'px) rotate(' +
          (pnx * 0.4).toFixed(3) + 'deg)';
        satsEl.style.transform =
          'translate(' + (pnx * 9).toFixed(2) + 'px,' + (pny * 7).toFixed(2) + 'px)';
      }
      raf = requestAnimationFrame(tick);
    }

    function onMouseMove(e) {
      const hero = e.target.closest ? e.target.closest('[data-hero="3a"]') : null;
      if (!hero) {
        tnx = 0;
        tny = 0;
        return;
      }
      const r = hero.getBoundingClientRect();
      tnx = ((e.clientX - r.left) / r.width - 0.5) * 2;
      tny = ((e.clientY - r.top) / r.height - 0.5) * 2;
    }

    document.addEventListener('mousemove', onMouseMove);
    raf = requestAnimationFrame(tick);
    return () => {
      document.removeEventListener('mousemove', onMouseMove);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  return (
    <div
      ref={moonRef}
      data-moon="3a"
      className="relative w-[500px] h-[500px] shrink-0 [&_svg]:w-full [&_svg]:h-full max-[780px]:w-[min(78vw,360px)] max-[780px]:h-[min(78vw,360px)]"
    >
      <div className="moon-disc"></div>
      <div className="moon-surface">
        <svg viewBox="0 0 500 500" width="500" height="500" style={{ position: 'absolute', inset: 0 }}>
          <defs>
            <pattern id="gStA" width="7" height="7" patternUnits="userSpaceOnUse">
              <circle cx="2" cy="2" r="0.9" fill="rgba(223,230,245,0.6)"></circle>
              <circle cx="5.4" cy="4.8" r="0.55" fill="rgba(223,230,245,0.45)"></circle>
            </pattern>
            <pattern id="gStB" width="11" height="11" patternUnits="userSpaceOnUse" patternTransform="rotate(24)">
              <circle cx="3" cy="3" r="0.85" fill="rgba(223,230,245,0.5)"></circle>
              <circle cx="8" cy="7.5" r="0.5" fill="rgba(223,230,245,0.38)"></circle>
            </pattern>
            <radialGradient id="gFade" cx="50%" cy="47%" r="62%">
              <stop offset="0" stopColor="#ffffff" stopOpacity="1"></stop>
              <stop offset="0.68" stopColor="#ffffff" stopOpacity="0.9"></stop>
              <stop offset="1" stopColor="#ffffff" stopOpacity="0"></stop>
            </radialGradient>
            <radialGradient id="gLimbFade" gradientUnits="userSpaceOnUse" cx="250" cy="250" r="247">
              <stop offset="0.76" stopColor="#ffffff" stopOpacity="0"></stop>
              <stop offset="0.93" stopColor="#ffffff" stopOpacity="0.55"></stop>
              <stop offset="1" stopColor="#ffffff" stopOpacity="0.85"></stop>
            </radialGradient>
            <mask id="gM1" maskUnits="userSpaceOnUse" x="0" y="0" width="500" height="500">
              <path d="M 100 85 C 130 62 185 60 212 85 C 235 105 232 140 208 158 C 180 178 132 175 110 152 C 88 130 82 102 100 85 Z" fill="url(#gFade)"></path>
            </mask>
            <mask id="gM2" maskUnits="userSpaceOnUse" x="0" y="0" width="500" height="500">
              <path d="M 62 218 C 84 204 108 214 114 238 C 122 264 110 286 114 310 C 118 336 104 356 80 352 C 56 348 44 322 48 294 C 52 268 44 238 62 218 Z" fill="url(#gFade)"></path>
            </mask>
            <mask id="gM3" maskUnits="userSpaceOnUse" x="0" y="0" width="500" height="500">
              <path d="M 330 130 C 356 120 384 130 390 155 C 396 180 380 200 353 202 C 327 204 311 186 313 161 C 315 143 318 137 330 130 Z" fill="url(#gFade)"></path>
            </mask>
            <mask id="gM4" maskUnits="userSpaceOnUse" x="0" y="0" width="500" height="500">
              <path d="M 388 232 C 410 226 428 238 430 260 C 432 282 414 296 392 294 C 370 292 358 274 364 254 C 368 240 376 236 388 232 Z" fill="url(#gFade)"></path>
            </mask>
            <mask id="gM5" maskUnits="userSpaceOnUse" x="0" y="0" width="500" height="500">
              <path d="M 225 390 C 242 384 258 392 260 408 C 262 424 248 434 232 432 C 216 430 208 416 212 404 C 215 396 218 393 225 390 Z" fill="url(#gFade)"></path>
            </mask>
            <mask id="gM6" maskUnits="userSpaceOnUse" x="0" y="0" width="500" height="500">
              <path d="M 218 235 C 244 222 280 226 294 248 C 308 270 298 298 272 308 C 246 318 214 310 204 286 C 195 264 200 246 218 235 Z" fill="url(#gFade)"></path>
            </mask>
            <mask id="gMLimb" maskUnits="userSpaceOnUse" x="0" y="0" width="500" height="500">
              <rect x="0" y="0" width="500" height="500" fill="url(#gLimbFade)"></rect>
            </mask>
          </defs>
          <rect x="0" y="0" width="500" height="500" fill="url(#gStA)" mask="url(#gM1)"></rect>
          <rect x="0" y="0" width="500" height="500" fill="url(#gStB)" mask="url(#gM2)"></rect>
          <rect x="0" y="0" width="500" height="500" fill="url(#gStA)" fillOpacity="0.85" mask="url(#gM3)"></rect>
          <rect x="0" y="0" width="500" height="500" fill="url(#gStB)" fillOpacity="0.95" mask="url(#gM4)"></rect>
          <rect x="0" y="0" width="500" height="500" fill="url(#gStB)" fillOpacity="0.8" mask="url(#gM5)"></rect>
          <rect x="0" y="0" width="500" height="500" fill="url(#gStA)" fillOpacity="0.6" mask="url(#gM6)"></rect>
          <path d="M 487.6 186.3 A 246 246 0 0 1 186.3 487.6 L 198.8 441.2 A 198 198 0 0 0 441.2 198.8 Z" fill="url(#gStB)" mask="url(#gMLimb)"></path>
          <circle cx="258" cy="62" r="8" fill="none" stroke="rgba(223,230,245,0.4)" strokeWidth="1" strokeDasharray="1.5 2.8"></circle>
          <ellipse cx="462" cy="238" rx="4.5" ry="9" fill="none" stroke="rgba(223,230,245,0.35)" strokeWidth="1" strokeDasharray="1.5 2.6" transform="rotate(4 462 238)"></ellipse>
          <circle cx="118" cy="352" r="7" fill="none" stroke="rgba(223,230,245,0.38)" strokeWidth="1" strokeDasharray="1.5 2.8"></circle>
          <circle cx="312" cy="428" r="6" fill="none" stroke="rgba(223,230,245,0.35)" strokeWidth="1" strokeDasharray="1.4 2.6"></circle>
          <circle cx="180" cy="205" r="10" fill="none" stroke="rgba(223,230,245,0.42)" strokeWidth="1" strokeDasharray="1.6 3"></circle>
          <circle cx="180" cy="205" r="4" fill="none" stroke="rgba(223,230,245,0.3)" strokeWidth="1" strokeDasharray="1.2 2.4"></circle>
          <circle cx="322" cy="352" r="8" fill="none" stroke="rgba(223,230,245,0.36)" strokeWidth="1" strokeDasharray="1.5 2.8"></circle>
        </svg>
      </div>
      <div className="moon-lighting"></div>
      <svg ref={satsRef} className="moon-sats" viewBox="0 0 500 500" width="500" height="500">
        <defs>
          <filter id="satGlow" x="-300%" y="-300%" width="700%" height="700%">
            <feGaussianBlur stdDeviation="3.2" result="b"></feGaussianBlur>
            <feMerge>
              <feMergeNode in="b"></feMergeNode>
              <feMergeNode in="SourceGraphic"></feMergeNode>
            </feMerge>
          </filter>
          <linearGradient id="satTrail1" x1="1" y1="0" x2="0" y2="0">
            <stop offset="0" stopColor="#ffffff" stopOpacity="0.85"></stop>
            <stop offset="1" stopColor="#ffffff" stopOpacity="0"></stop>
          </linearGradient>
          <linearGradient id="satTrail2" x1="1" y1="0" x2="0" y2="0">
            <stop offset="0" stopColor="#cdd6ea" stopOpacity="0.7"></stop>
            <stop offset="1" stopColor="#cdd6ea" stopOpacity="0"></stop>
          </linearGradient>
        </defs>
        <g transform="translate(250,250) rotate(-18)">
          <path d="M -288 0 A 288 110 0 0 0 288 0" fill="none" stroke="rgba(158,170,198,0.13)" strokeWidth="1" strokeDasharray="5 7"></path>
          <path d="M -288 0 A 288 110 0 0 1 288 0" fill="none" stroke="rgba(158,170,198,0.38)" strokeWidth="1" strokeDasharray="5 7"></path>
          <g>
            <animateMotion dur="15s" repeatCount="indefinite" rotate="auto" path="M -288 0 A 288 110 0 0 1 288 0 A 288 110 0 0 1 -288 0 Z"></animateMotion>
            <animate attributeName="opacity" values="1;1;0.08;0.08;1;1" keyTimes="0;0.58;0.68;0.82;0.92;1" calcMode="spline" keySplines="0.4 0 0.6 1;0.4 0 0.6 1;0.4 0 0.6 1;0.4 0 0.6 1;0.4 0 0.6 1" dur="15s" repeatCount="indefinite"></animate>
            <path d="M 0 -2.6 L -34 0 L 0 2.6 Z" fill="url(#satTrail1)"></path>
            <g filter="url(#satGlow)"><circle r="4.8" fill="#ffffff"></circle></g>
          </g>
        </g>
        <g transform="translate(250,250) rotate(32)">
          <path d="M -288 0 A 288 110 0 0 0 288 0" fill="none" stroke="rgba(158,170,198,0.11)" strokeWidth="1" strokeDasharray="5 7"></path>
          <path d="M -288 0 A 288 110 0 0 1 288 0" fill="none" stroke="rgba(158,170,198,0.30)" strokeWidth="1" strokeDasharray="5 7"></path>
          <g>
            <animateMotion dur="21s" repeatCount="indefinite" rotate="auto" path="M -288 0 A 288 110 0 0 1 288 0 A 288 110 0 0 1 -288 0 Z"></animateMotion>
            <animate attributeName="opacity" values="1;1;0.08;0.08;1;1" keyTimes="0;0.58;0.68;0.82;0.92;1" calcMode="spline" keySplines="0.4 0 0.6 1;0.4 0 0.6 1;0.4 0 0.6 1;0.4 0 0.6 1;0.4 0 0.6 1" dur="21s" repeatCount="indefinite"></animate>
            <path d="M 0 -2.2 L -28 0 L 0 2.2 Z" fill="url(#satTrail2)"></path>
            <g filter="url(#satGlow)"><circle r="3.6" fill="#cdd6ea"></circle></g>
          </g>
        </g>
      </svg>
    </div>
  );
}
